import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Info } from 'lucide-react';
import { formatVolume, formatPremium } from './formatters';

const FlowRow = ({ flow, symbol }) => (
  <div
    key={`${flow.symbol || ''}-${flow.strike}-${flow.expiry}-${flow.timestamp || flow.time}`}
    className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
  >
    <div className="flex items-center gap-3">
      <span
        className={`font-bold text-lg px-3 py-1 rounded ${
          flow.action === 'Buy' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}
      >
        {symbol}
      </span>
      <div>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-900">
            {flow.action} {flow.strike} {flow.type}
          </span>
          {flow.is_sweep && <Badge className="bg-orange-500 text-white text-xs px-2 py-0">SWEEP</Badge>}
        </div>
        <p className="text-sm text-gray-600">{flow.expiry}</p>
      </div>
    </div>
    <div className="text-right">
      <p className="font-bold text-lg text-gray-900">{formatPremium(flow.premium)}</p>
      <p className="text-xs text-gray-500">{flow.time}</p>
    </div>
  </div>
);

const OptionsFlowCard = ({ optionsFlow, stockSymbol }) => {
  if (!optionsFlow?.flows?.length) return null;
  const avgPremium = optionsFlow.total_premium / optionsFlow.flows.length;
  const cpRatio =
    optionsFlow.put_volume > 0 ? (optionsFlow.call_volume / optionsFlow.put_volume).toFixed(2) : 'N/A';
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Unusual Options Flow</CardTitle>
            <p className="text-sm text-gray-600 mt-1">
              {optionsFlow.flow_type} stocks - showing flows over {formatPremium(optionsFlow.min_threshold)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-600">Total Premium</p>
            <p className="text-xl font-bold text-purple-600">{formatPremium(optionsFlow.total_premium)}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full" />
            <span className="text-gray-600">Call Volume: {formatVolume(optionsFlow.call_volume)}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-full" />
            <span className="text-gray-600">Put Volume: {formatVolume(optionsFlow.put_volume)}</span>
          </div>
        </div>

        <div className="space-y-2">
          {optionsFlow.flows.map((flow) => (
            <FlowRow
              key={`${flow.symbol || ''}-${flow.strike}-${flow.expiry}-${flow.timestamp || flow.time}`}
              flow={flow}
              symbol={stockSymbol}
            />
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-xs text-gray-600">Avg Premium</p>
            <p className="font-bold text-gray-900">{formatPremium(avgPremium)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600">Total Flows</p>
            <p className="font-bold text-gray-900">{optionsFlow.flows.length}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600">Call/Put Ratio</p>
            <p className="font-bold text-gray-900">{cpRatio}</p>
          </div>
        </div>

        <div className="mt-3 flex items-start gap-2 text-xs text-gray-500">
          <Info size={14} className="mt-0.5 text-gray-400" />
          <p>
            Options data is derived from Yahoo Finance option chains using notional premium filters. It does not
            represent a complete picture of all market activity and should not be treated as personalized trading
            advice.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default OptionsFlowCard;
